import { apiGet, apiPatch } from '../api.ts';
import { createUrl } from '@/util/url.ts';
import { QueryCacheKeys } from '@/api/queryKeys.ts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

/** 미처리된 알람 응답 타입 */
export interface IAlertResponse {
    emergency?: { id: string; type: string; timestamp: string; description: string }[];
    bio?: { id: string; type: string; timestamp: string; description: string }[];
    fall?: { id: string; type: string; timestamp: string; description: string }[];
}

/** 기본값 설정 */
const DEFAULT_ALERTS: IAlertResponse = {
    emergency: [],
    bio: [],
    fall: [],
};

/** GET 요청: 안심구역 상태 가져오기 */
export const apiGetSafeZoneStatus = async (): Promise<{ isSafeZone: boolean | null }> => {
    try {
        const response = await apiGet<{ isSafeZone?: boolean }>(createUrl('/alert/safe-zone', {}));
        return { isSafeZone: response.data?.isSafeZone ?? null }; // ⬅ 수정됨
    } catch (error: unknown) {
        if (error instanceof Error && 'response' in error) {
            const errResponse = (error as any).response;
            if (errResponse?.status === 404) {
                return { isSafeZone: null }; // "알 수 없음" 처리
            }
        }
        console.error('안심구역 상태 API 요청 실패:', error);
        return { isSafeZone: null };
    }
};

/** React Query Hook: 안심구역 상태 조회 */
export const useQueryGetSafeZoneStatus = () => {
    return useQuery({
        queryKey: QueryCacheKeys.alerts.safeZoneStatus(),
        queryFn: apiGetSafeZoneStatus,
        refetchInterval: 10000, // 30초에서 10초로 줄임 (더 자주 갱신)
        staleTime: 5000, // 25초에서 5초로 줄임 (더 빨리 만료되도록)
        refetchOnWindowFocus: true, // 창 포커스 변경 시에도 갱신
    });
};

/** GET 요청: 미처리 알람 가져오기 */
export const apiGetAlertsUnprocessed = async (): Promise<IAlertResponse> => {
    try {
        const { data } = await apiGet<IAlertResponse>(createUrl('/alert/unprocessed', {}));
        return data ?? DEFAULT_ALERTS; // `null`이면 기본값 반환
    } catch (error) {
        console.error('미처리 알람 API 요청 실패:', error);
        return DEFAULT_ALERTS;
    }
};

/** React Query Hook: 미처리 알람 조회 */
const useQueryGetAlertsUnprocessed = () => {
    return useQuery({
        queryKey: QueryCacheKeys.alerts.unprocessed(),
        queryFn: apiGetAlertsUnprocessed,
        refetchInterval: 2000, // 5초에서 2초로 변경
        staleTime: 0, // 캐시를 사용하지 않고 항상 최신 데이터 사용
        refetchOnWindowFocus: true,
    });
};

export default useQueryGetAlertsUnprocessed;

/** PATCH 요청: 미처리 알림 일괄 처리 */
export const apiPatchProcessAlerts = async (alertType: string): Promise<{ success: boolean }> => {
    try {
        const { data } = await apiPatch<{ success: boolean }>(createUrl(`/alert/process`, {}), { alert_type: alertType });
        return data ?? { success: false }; // 기본값 반환
    } catch (error) {
        console.error("알림 처리 요청 실패:", error);
        return { success: false }; // 요청 실패 시 기본값 반환
    }
};

/** React Query Mutation Hook: 알람 처리 */
export const useMutationProcessAlerts = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (alertType: string) => apiPatchProcessAlerts(alertType),
        onSuccess: () => {
            // 모든 관련 데이터 즉시 갱신
            Promise.all([
                queryClient.invalidateQueries({ queryKey: QueryCacheKeys.alerts.unprocessed() }),
                queryClient.invalidateQueries({ queryKey: QueryCacheKeys.user.getUsers() }),
                queryClient.invalidateQueries({ queryKey: QueryCacheKeys.notification.getAlertBioLive() })
            ]).then(() => {
                // 즉시 리패치 실행
                queryClient.refetchQueries({ queryKey: QueryCacheKeys.alerts.unprocessed() });
                queryClient.refetchQueries({ queryKey: QueryCacheKeys.user.getUsers() });
                queryClient.refetchQueries({ queryKey: QueryCacheKeys.notification.getAlertBioLive() });
            });
        },
    });
};
